import React, { memo, useMemo } from 'react';

import { IconButton, Typography } from '@mui/material';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { ResetIcon, UserEditIcon, UserRejectIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import dayjs from 'dayjs';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';
import { BadgeWrapper } from '../../sharedComponents';

const LoanRole = ({
  loanRole,
  isLoading,
  onUnassignLoanRole,
  onOpenReassignLoanRoleForm,
  onRestoreLoanRole,
  isEditAllowed,
  isManageAllowed,
  isActionableForCurrentSubCompany = true,
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const {
    isAnnihilated,
    isNewAssigned,
    isUnassigned,
    isInitiallyNewAssigned,
    isInitiallyUnassigned,
    isInitiallyReassigned,
    isReassigned,
    assignedDate,
    userName,
    roleName,
    loanRoleId,
  } = loanRole;

  const isUnknownDate =
    isNewAssigned || isInitiallyNewAssigned || isReassigned || isInitiallyReassigned;

  const isHighlighted = [
    isNewAssigned,
    isUnassigned,
    isInitiallyNewAssigned,
    isInitiallyUnassigned,
    (isInitiallyReassigned || isReassigned) && !isAnnihilated,
  ].some(Boolean);

  const items = useMemo(
    () => [
      ...getConditionalArray(isEditAllowed, [
        {
          text: 'Reassign',
          onClick: () => onOpenReassignLoanRoleForm(loanRoleId, userName),
          icon: <UserEditIcon size={IconSize[20]} />,
        },
      ]),

      ...getConditionalArray(isManageAllowed, [
        {
          text: 'Unassign',
          onClick: () => onUnassignLoanRole(loanRoleId),
          icon: <UserRejectIcon size={IconSize[20]} />,
        },
      ]),
    ],
    [
      onUnassignLoanRole,
      loanRoleId,
      userName,
      onOpenReassignLoanRoleForm,
      isManageAllowed,
      isEditAllowed,
    ],
  );

  // DEV-4720: a broker sees every assignment on the loan, but may only act on roles exposed to /
  // owned by its subco — non-actionable (lender-only, e.g. Underwriter) rows render read-only with
  // no kebab/restore affordance. Lender/employee users pass isActionableForCurrentSubCompany=true.
  const isControlVisible = (isManageAllowed || isEditAllowed) && isActionableForCurrentSubCompany;

  return (
    <BadgeWrapper
      control={
        isControlVisible &&
        (isUnassigned || isInitiallyUnassigned ? (
          <IconButton
            color="primary.main"
            data-loan-role-id={loanRoleId}
            data-testid={PropertyAndLoanTestIds.getLoanRoleRestoreButtonId(loanRoleId)}
            disabled={isLoading || isInitiallyUnassigned}
            key={`${loanRoleId}-restore`}
            onClick={onRestoreLoanRole}
          >
            <ResetIcon size={IconSize[24]} />
          </IconButton>
        ) : (
          <>
            <MoreButtonCDS
              disabled={isLoading}
              isSelected={!!anchorEl}
              key={`${loanRoleId}-action`}
              onClick={onSetAnchorElement}
            />

            <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
          </>
        ))
      }
      isDeleted={!!(isUnassigned || isInitiallyUnassigned)}
      isHighlighted={isHighlighted}
      textContent={
        <>
          <Typography
            data-truncated
            className="truncate overflow-hidden"
            color={isHighlighted ? 'text.tableHighlight' : 'text.black'}
            component="p"
            variant="regular14"
          >
            {roleName}
          </Typography>
          <Typography
            data-truncated
            className="truncate overflow-hidden"
            color={isHighlighted ? 'text.tableHighlight' : 'text.black'}
            component="p"
            variant="bold16"
          >
            {userName}
          </Typography>
          <Typography
            data-truncated
            className="truncate overflow-hidden pr-4"
            color={isUnknownDate ? 'text.tableHighlight' : 'text.dark'}
            component="p"
            variant="regularItalic12"
          >
            {isUnknownDate
              ? 'Date will be updated'
              : dayjs(assignedDate).format(COMMON_DATETIME_FORMAT)}
          </Typography>
        </>
      }
    />
  );
};

LoanRole.propTypes = {
  loanRole: PropTypes.object.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isManageAllowed: PropTypes.bool.isRequired,
  onUnassignLoanRole: PropTypes.func.isRequired,
  onOpenReassignLoanRoleForm: PropTypes.func.isRequired,
  onRestoreLoanRole: PropTypes.func.isRequired,
  isActionableForCurrentSubCompany: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default memo(LoanRole);
